/*
 * <social-videos> — feed-style clips.
 *
 * Each <video> carries its file in data-src, so nothing downloads until the
 * clip scrolls near. On screen it plays muted and looped; off screen it
 * pauses. The sound button (or a tap anywhere on the clip) turns its sound on
 * and mutes every other clip, so the section never talks over itself.
 *
 * With reduced motion nothing plays by itself: a tap starts the clip.
 */
if (!customElements.get('social-videos')) {
  customElements.define(
    'social-videos',
    class SocialVideos extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.frames = Array.prototype.slice.call(this.querySelectorAll('[data-ref="frame"]'));

        this.frames.forEach(
          function (frame) {
            var video = frame.querySelector('[data-ref="video"]');
            var button = frame.querySelector('[data-ref="sound"]');
            if (!video) return;
            if (button) button.addEventListener('click', this.toggleSound.bind(this, frame));
            video.addEventListener('click', this.toggleSound.bind(this, frame));
          }.bind(this)
        );

        if (!('IntersectionObserver' in window)) {
          this.frames.forEach(this.load);
          return;
        }

        // Load a little before the clip arrives; play only while it is mostly visible.
        this.loader = new IntersectionObserver(
          function (entries) {
            entries.forEach(
              function (e) {
                if (!e.isIntersecting) return;
                this.load(e.target);
                this.loader.unobserve(e.target);
              }.bind(this)
            );
          }.bind(this),
          { rootMargin: '300px 0px' }
        );
        this.player = new IntersectionObserver(
          function (entries) {
            entries.forEach(
              function (e) {
                var video = e.target.querySelector('[data-ref="video"]');
                if (!video) return;
                if (e.isIntersecting && !this.still) this.play(video);
                else if (!e.isIntersecting) video.pause();
              }.bind(this)
            );
          }.bind(this),
          { threshold: 0.6 }
        );
        this.frames.forEach(
          function (frame) {
            this.loader.observe(frame);
            this.player.observe(frame);
          }.bind(this)
        );
      }

      disconnectedCallback() {
        if (this.loader) this.loader.disconnect();
        if (this.player) this.player.disconnect();
      }

      load(frame) {
        var video = frame.querySelector('[data-ref="video"]');
        if (!video || video.src || !video.dataset.src) return;
        video.src = video.dataset.src;
      }

      play(video) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }

      toggleSound(frame) {
        var video = frame.querySelector('[data-ref="video"]');
        if (!video) return;
        this.load(frame);
        var turnOn = video.muted || video.paused;

        this.frames.forEach(
          function (other) {
            if (other === frame) return;
            var v = other.querySelector('[data-ref="video"]');
            if (v && !v.muted) this.setSound(other, false);
          }.bind(this)
        );

        this.setSound(frame, turnOn);
        if (turnOn) this.play(video);
      }

      setSound(frame, on) {
        var video = frame.querySelector('[data-ref="video"]');
        var button = frame.querySelector('[data-ref="sound"]');
        var label = frame.querySelector('[data-ref="sound-label"]');
        video.muted = !on;
        frame.classList.toggle('is-sound-on', on);
        if (button) button.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (label) label.textContent = on ? this.dataset.soundOff || 'Turn sound off' : this.dataset.soundOn || 'Turn sound on';
      }
    }
  );
}
